/**
 * Single Angular service for the real estate agency feature module.
 *
 * No component talks to Firestore/Functions directly — collection paths,
 * query shape and callable wiring all live here. Mirrors `SyndicService`.
 *
 * Phase 1 surface: agency profile + dashboard stats + onboarding callables.
 * Later phases extend this with agencyProperties$, leads$, viewings$, mandates$ and
 * their CRUD + portal-connector callables.
 */

import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  collectionGroup,
  collectionData,
  doc,
  docData,
  getDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  setDoc,
  updateDoc,
  deleteField,
  Timestamp,
  serverTimestamp,
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable, map } from 'rxjs';
import { mandatesForProperty } from '../mandates/mandate-coverage.util';
import { viewingsWindowStart } from '../viewings/viewings-window.util';
import { WorkspaceStore } from '../../../shared/services/workspace.store';
import { AuthStore } from '@core/services/auth/auth.store';
import { AuthReadyService } from '@core/services/auth/auth-ready.service';
import { deepStripUndefined } from '../../../shared/utils/firestore-clean';
import {
  AgencyProfile,
  AgencyProfileInput,
  AgencyStats,
} from '../../../shared/definitions/agency-profile';
import { AgencyProperty, AgencyPropertyIndex, AgencyPropertySummary } from '../../../shared/definitions/agency-property';
import { AgencyMandate, MandateTermsInput } from '../../../shared/definitions/agency-mandate';
import { AgencyLead, LeadStage } from '../../../shared/definitions/agency-lead';
import {
  LeadActivity,
  LeadActivityKind,
  LeadActivityMeta,
  LeadActivityType,
  ManualLeadActivityType,
} from '../../../shared/definitions/lead-activity';
import { LeadTask } from '../../../shared/definitions/lead-task';
import { AgencyDeal, DealType, defaultDealMilestones } from '../../../shared/definitions/agency-deal';
import { Viewing } from '../../../shared/definitions/viewing';
import { PortalConnector, PortalId } from '../../../shared/definitions/portal-connector';
import {
  AgencyTeam,
  AgencyTeamMember,
  AgencyMemberRoleInput,
  AgencyTeamInput,
} from '../../../shared/definitions/agency-team';
import { AgencyEstimation, EstimationAiValuation } from '../../../shared/definitions/agency-estimation';

/**
 * Safety cap on agency list streams. Lists filter by scope client-side AFTER
 * the read, so the cap is generous (not a per-scope server limit) — it only
 * bounds runaway reads for very large workspaces. A true scope-aware paginated
 * query is a follow-up; the recent-properties cache (1 read) handles the biggest
 * list. Keep high enough that a délégué's items aren't starved by colleagues'.
 */
const AGENCY_LIST_LIMIT = 500;

/** Per-lead activity feed cap (most recent). */
const LEAD_ACTIVITY_LIMIT = 200;

// Client Firestore writes run WITHOUT `ignoreUndefinedProperties`, so every
// write below strips `undefined` via the canonical `deepStripUndefined`
// (src/app/shared/utils/firestore-clean.ts). FieldValue sentinels
// (`serverTimestamp()`, `deleteField()`) are always added AFTER stripping,
// never passed through it.

@Injectable({ providedIn: 'root' })
export class AgencyService {
  private readonly db = inject(Firestore);
  private readonly functions = inject(Functions);
  private readonly workspaceStore = inject(WorkspaceStore);
  private readonly authStore = inject(AuthStore);
  private readonly authReady = inject(AuthReadyService);

  // -------------------------------------------------------------------------
  // Path helpers
  // -------------------------------------------------------------------------

  /** Current workspace id, asserting it is an agency workspace. */
  get agencyWorkspaceId(): string {
    const ws = this.workspaceStore.currentWorkspace();
    if (!ws) {
      throw new Error('AgencyService: no current workspace');
    }
    if (ws.type !== 'agency') {
      throw new Error(
        `AgencyService: current workspace is type=${ws.type}, expected 'agency'`,
      );
    }
    if (!ws.workspaceId) {
      throw new Error('AgencyService: workspace has no workspaceId');
    }
    return ws.workspaceId;
  }

  private profilePath(): string {
    return `workspaces/${this.agencyWorkspaceId}/agency_profile/main`;
  }
  private listingsPath(): string {
    return `workspaces/${this.agencyWorkspaceId}/agency_properties`;
  }
  private mandatesPath(): string {
    return `workspaces/${this.agencyWorkspaceId}/agency_mandates`;
  }
  private leadsPath(): string {
    return `workspaces/${this.agencyWorkspaceId}/agency_leads`;
  }
  private viewingsPath(): string {
    return `workspaces/${this.agencyWorkspaceId}/agency_viewings`;
  }
  private connectorsPath(): string {
    return `workspaces/${this.agencyWorkspaceId}/portal_connections`;
  }
  private teamMembersPath(): string {
    return `workspaces/${this.agencyWorkspaceId}/agency_team_members`;
  }
  private teamsPath(): string {
    return `workspaces/${this.agencyWorkspaceId}/agency_teams`;
  }
  private estimationsPath(): string {
    return `workspaces/${this.agencyWorkspaceId}/agency_estimations`;
  }

  // -------------------------------------------------------------------------
  // Observables — profile
  // -------------------------------------------------------------------------

  profile$(): Observable<AgencyProfile | undefined> {
    return docData(doc(this.db, this.profilePath()), {
      idField: 'profileId',
    }) as Observable<AgencyProfile | undefined>;
  }

  // -------------------------------------------------------------------------
  // Callable wrappers
  // -------------------------------------------------------------------------

  async updateProfile(profile: AgencyProfileInput): Promise<{
    success: boolean;
    profileCompleteness: number;
    onboardingCompleted: boolean;
  }> {
    await this.authReady.whenAuthReady();
    const fn = httpsCallable<
      { workspaceId: string; profile: AgencyProfileInput },
      { success: boolean; profileCompleteness: number; onboardingCompleted: boolean }
    >(this.functions, 'updateAgencyProfile');
    // The callable SDK encodes `undefined` as `null`, which the backend schema
    // rejects for optional strings ("expected string, received null"). Drop the
    // undefined keys first; the payload only holds strings, numbers and arrays.
    const clean = JSON.parse(JSON.stringify(profile)) as AgencyProfileInput;
    const res = await fn({ workspaceId: this.agencyWorkspaceId, profile: clean });
    return res.data;
  }

  async completeOnboarding(): Promise<{ success: boolean; message: string }> {
    await this.authReady.whenAuthReady();
    const fn = httpsCallable<
      { workspaceId: string },
      { success: boolean; message: string }
    >(this.functions, 'completeAgencyOnboarding');
    const res = await fn({ workspaceId: this.agencyWorkspaceId });
    return res.data;
  }

  async getStats(): Promise<AgencyStats> {
    const fn = httpsCallable<
      { workspaceId: string },
      { success: boolean; stats: AgencyStats }
    >(this.functions, 'getAgencyStats');
    const res = await fn({ workspaceId: this.agencyWorkspaceId });
    return res.data.stats;
  }

  // -------------------------------------------------------------------------
  // Archive / soft-delete (restorable for 30 days, then purged server-side)
  // -------------------------------------------------------------------------

  /** Days an archived agency entity is kept before permanent deletion. */
  static readonly ARCHIVE_RETENTION_DAYS = 30;

  /** Stamp `isDeleted` + a 30-day `permanentDeletionDate` on a doc (archive). */
  private async archiveDoc(path: string, id: string): Promise<void> {
    const purgeMs = Date.now() + AgencyService.ARCHIVE_RETENTION_DAYS * 24 * 60 * 60 * 1000;
    await updateDoc(doc(this.db, `${path}/${id}`), {
      isDeleted: true,
      deletedAt: serverTimestamp(),
      permanentDeletionDate: Timestamp.fromMillis(purgeMs),
      deletedBy: this.authStore.user()?.userUID ?? null,
      updatedAt: serverTimestamp(),
    });
  }

  /** Clear the soft-delete stamps (restore within the 30-day window). */
  private async restoreDoc(path: string, id: string): Promise<void> {
    await updateDoc(doc(this.db, `${path}/${id}`), {
      isDeleted: deleteField(),
      deletedAt: deleteField(),
      permanentDeletionDate: deleteField(),
      deletedBy: deleteField(),
      updatedAt: serverTimestamp(),
    });
  }

  archiveProperty(id: string): Promise<void> { return this.archiveDoc(this.listingsPath(), id); }
  restoreProperty(id: string): Promise<void> { return this.restoreDoc(this.listingsPath(), id); }
  archiveLead(id: string): Promise<void> { return this.archiveDoc(this.leadsPath(), id); }
  restoreLead(id: string): Promise<void> { return this.restoreDoc(this.leadsPath(), id); }
  archiveDeal(id: string): Promise<void> { return this.archiveDoc(this.dealsPath(), id); }
  restoreDeal(id: string): Promise<void> { return this.restoreDoc(this.dealsPath(), id); }
  archiveViewing(id: string): Promise<void> { return this.archiveDoc(this.viewingsPath(), id); }
  restoreViewing(id: string): Promise<void> { return this.restoreDoc(this.viewingsPath(), id); }

  // -------------------------------------------------------------------------
  // Listings — client-side CRUD (rules: canEditWorkspace via catch-all)
  // -------------------------------------------------------------------------

  agencyProperties$(): Observable<AgencyProperty[]> {
    const ref = collection(this.db, this.listingsPath());
    return collectionData(query(ref, orderBy('updatedAt', 'desc'), limit(AGENCY_LIST_LIMIT)), {
      idField: 'propertyId',
    }) as Observable<AgencyProperty[]>;
  }

  /**
   * The 100 most-recent LIVE biens as lightweight summaries — a SINGLE doc read
   * (maintained by the `onAgencyPropertyIndexSync` trigger) instead of streaming
   * the whole collection. Drives the default property-list view. Archived biens
   * are excluded; for older biens beyond the cap, use a full search.
   */
  recentProperties$(): Observable<AgencyPropertySummary[]> {
    return docData(
      doc(this.db, `workspaces/${this.agencyWorkspaceId}/agency_property_index/recent`),
    ).pipe(map((d) => ((d as AgencyPropertyIndex | undefined)?.items ?? [])));
  }

  /** Archived (soft-deleted) biens — the restore bin. Equality-only; bounded. */
  archivedProperties$(): Observable<AgencyProperty[]> {
    const ref = collection(this.db, this.listingsPath());
    return collectionData(query(ref, where('isDeleted', '==', true), limit(AGENCY_LIST_LIMIT)), {
      idField: 'propertyId',
    }) as Observable<AgencyProperty[]>;
  }

  agencyProperty$(propertyId: string): Observable<AgencyProperty | undefined> {
    return docData(doc(this.db, `${this.listingsPath()}/${propertyId}`), {
      idField: 'propertyId',
    }) as Observable<AgencyProperty | undefined>;
  }

  /** Create a listing; returns the new id. Generates a reference if absent. */
  async createAgencyProperty(input: Partial<AgencyProperty>): Promise<string> {
    const ref = doc(collection(this.db, this.listingsPath()));
    const propertyId = ref.id;
    const reference = input.reference || `AG-${propertyId.slice(0, 6).toUpperCase()}`;
    await setDoc(ref, {
      ...deepStripUndefined({
        ...input,
        propertyId,
        workspaceId: this.agencyWorkspaceId,
        reference,
        status: input.status ?? 'draft',
      }),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return propertyId;
  }

  async updateAgencyProperty(propertyId: string, patch: Partial<AgencyProperty>): Promise<void> {
    await updateDoc(doc(this.db, `${this.listingsPath()}/${propertyId}`), {
      ...deepStripUndefined(patch),
      updatedAt: serverTimestamp(),
    });
  }

  async deleteAgencyProperty(propertyId: string): Promise<void> {
    await deleteDoc(doc(this.db, `${this.listingsPath()}/${propertyId}`));
  }

  // -------------------------------------------------------------------------
  // Mandates — read-only mirror (engine) + agency-editable terms (callable)
  // -------------------------------------------------------------------------

  mandates$(): Observable<AgencyMandate[]> {
    const ref = collection(this.db, this.mandatesPath());
    return collectionData(query(ref, orderBy('updatedAt', 'desc'), limit(AGENCY_LIST_LIMIT)), {
      idField: 'mandateId',
    }) as Observable<AgencyMandate[]>;
  }

  mandate$(mandateId: string): Observable<AgencyMandate | undefined> {
    return docData(doc(this.db, `${this.mandatesPath()}/${mandateId}`), {
      idField: 'mandateId',
    }) as Observable<AgencyMandate | undefined>;
  }

  /**
   * Mandates attached to a given bien (either link direction:
   * agency_created `propertyId` or owner_shared `generatedPropertyId`). Derived
   * client-side from `mandates$()` — no extra Firestore query/index needed, and
   * it transparently picks up a bien's multiple mandates over time.
   */
  mandatesForProperty$(propertyId: string): Observable<AgencyMandate[]> {
    return this.mandates$().pipe(
      map((mandates) => mandatesForProperty(propertyId, mandates)),
    );
  }

  // -------------------------------------------------------------------------
  // Callable wrappers — mandate terms + listing-from-mandate + invitations
  // -------------------------------------------------------------------------

  async updateMandateTerms(mandateId: string, terms: MandateTermsInput): Promise<void> {
    const fn = httpsCallable<
      { workspaceId: string; mandateId: string; terms: MandateTermsInput },
      { success: boolean }
    >(this.functions, 'updateMandateTerms');
    await fn({ workspaceId: this.agencyWorkspaceId, mandateId, terms });
  }

  async createPropertyFromMandate(mandateId: string): Promise<{ propertyId: string; alreadyExists: boolean }> {
    const fn = httpsCallable<
      { workspaceId: string; mandateId: string },
      { success: boolean; propertyId: string; alreadyExists: boolean }
    >(this.functions, 'createPropertyFromMandate');
    const res = await fn({ workspaceId: this.agencyWorkspaceId, mandateId });
    return { propertyId: res.data.propertyId, alreadyExists: res.data.alreadyExists };
  }

  /** Create a first-class mandate on an agency-created bien (to be signed by the owner). */
  async createAgencyMandate(input: {
    propertyId: string;
    signerName: string;
    signerEmail: string;
    terms: MandateTermsInput;
  }): Promise<string> {
    const fn = httpsCallable<
      { workspaceId: string; propertyId: string; signerName: string; signerEmail: string; terms: MandateTermsInput },
      { success: boolean; mandateId: string }
    >(this.functions, 'createAgencyMandate');
    const res = await fn({ workspaceId: this.agencyWorkspaceId, ...input });
    return res.data.mandateId;
  }

  /** Render the mandate document (A4 HTML) and store it on the mandate. */
  async generateMandateDocument(
    mandateId: string,
    language?: string,
  ): Promise<{ html: string; fileName: string; language: string; warnings: string[] }> {
    const fn = httpsCallable<
      { workspaceId: string; mandateId: string; language?: string },
      { success: boolean; html: string; fileName: string; language: string; warnings: string[] }
    >(this.functions, 'generateAgencyMandateDocument');
    const res = await fn({ workspaceId: this.agencyWorkspaceId, mandateId, language });
    return res.data;
  }

  /** Send an agency-created mandate to its signer (owner) for e-signature. */
  async sendMandateForSignature(
    mandateId: string,
  ): Promise<{ sent: boolean; token: string; url: string }> {
    const fn = httpsCallable<
      { workspaceId: string; mandateId: string },
      { success: boolean; sent: boolean; token: string; url: string }
    >(this.functions, 'sendAgencyMandateForSignature');
    const res = await fn({ workspaceId: this.agencyWorkspaceId, mandateId });
    return res.data;
  }

  /**
   * Invite a property owner (by email) to join PROPORIO, create their property
   * and share it back with this agency. Mirror of the owner-side
   * `sendAgencyMandateInvitation`; once the owner shares, the property lands in
   * this workspace's mandates.
   */
  async inviteOwnerToShare(input: { ownerEmail: string; personalMessage?: string }): Promise<{ emailSent: boolean }> {
    const fn = httpsCallable<
      { agencyWorkspaceId: string; ownerEmail: string; personalMessage?: string },
      { success: boolean; emailSent: boolean }
    >(this.functions, 'inviteOwnerToShareProperty');
    const res = await fn({ agencyWorkspaceId: this.agencyWorkspaceId, ...input });
    return { emailSent: res.data.emailSent };
  }

  /** Preview a mandate invitation by token (no workspace context needed). */
  async lookupMandateInvitation(token: string): Promise<{
    status: string; propertyName: string | null; agencyEmail: string | null;
    personalMessage: string | null; expiresAt: string | null;
  }> {
    const fn = httpsCallable<
      { token: string },
      { success: boolean; status: string; propertyName: string | null; agencyEmail: string | null; personalMessage: string | null; expiresAt: string | null }
    >(this.functions, 'lookupAgencyMandateInvitation');
    const res = await fn({ token });
    return res.data;
  }

  /** Accept an owner→agency mandate invitation into THIS agency workspace. */
  async acceptMandateInvitation(invitationToken: string): Promise<{
    success: boolean; mandateId: string; ownerPropertyId: string;
  }> {
    const fn = httpsCallable<
      { invitationToken: string; agencyWorkspaceId: string },
      { success: boolean; linkId: string; mandateId: string; ownerPropertyId: string }
    >(this.functions, 'acceptAgencyMandateInvitation');
    const res = await fn({ invitationToken, agencyWorkspaceId: this.agencyWorkspaceId });
    return { success: res.data.success, mandateId: res.data.mandateId, ownerPropertyId: res.data.ownerPropertyId };
  }

  // -------------------------------------------------------------------------
  // Leads (mini-CRM) — client-side CRUD (rules: canEditWorkspace via catch-all)
  // -------------------------------------------------------------------------

  leads$(): Observable<AgencyLead[]> {
    const ref = collection(this.db, this.leadsPath());
    return collectionData(query(ref, orderBy('updatedAt', 'desc'), limit(AGENCY_LIST_LIMIT)), {
      idField: 'leadId',
    }) as Observable<AgencyLead[]>;
  }

  lead$(leadId: string): Observable<AgencyLead | undefined> {
    return docData(doc(this.db, `${this.leadsPath()}/${leadId}`), {
      idField: 'leadId',
    }) as Observable<AgencyLead | undefined>;
  }

  /** Lowercased, de-duplicated contact emails → indexed dedup (array-contains). */
  private contactEmailsLower(contacts: AgencyLead['contacts'] | undefined): string[] {
    return Array.from(
      new Set(
        (contacts ?? [])
          .map((c) => (c.email ?? '').trim().toLowerCase())
          .filter(Boolean),
      ),
    );
  }

  async createLead(input: Partial<AgencyLead>): Promise<string> {
    const ref = doc(collection(this.db, this.leadsPath()));
    const leadId = ref.id;
    await setDoc(ref, {
      ...deepStripUndefined({
        ...input,
        leadId,
        workspaceId: this.agencyWorkspaceId,
        stage: input.stage ?? 'new',
        contactEmailsLower: this.contactEmailsLower(input.contacts),
      }),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    await this.appendActivity(leadId, { kind: 'system', type: 'lead_created' });
    return leadId;
  }

  async updateLead(leadId: string, patch: Partial<AgencyLead>): Promise<void> {
    // Keep the indexed dedup field in sync whenever contacts change.
    const sync = patch.contacts
      ? { contactEmailsLower: this.contactEmailsLower(patch.contacts) }
      : {};
    await updateDoc(doc(this.db, `${this.leadsPath()}/${leadId}`), {
      ...deepStripUndefined({ ...patch, ...sync }),
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Move a lead to a new stage and auto-log the transition on its timeline.
   * Centralised here so every caller (kanban drag, lead detail, viewing
   * feedback) records the system event without duplicating the logging.
   */
  async updateLeadStage(leadId: string, stage: LeadStage): Promise<void> {
    const ref = doc(this.db, `${this.leadsPath()}/${leadId}`);
    const snap = await getDoc(ref);
    const fromStage = (snap.data() as AgencyLead | undefined)?.stage;
    if (fromStage === stage) return;
    await updateDoc(ref, { stage, updatedAt: serverTimestamp() });
    await this.appendActivity(leadId, { kind: 'system', type: 'stage_change', meta: { fromStage, toStage: stage } });
    if (stage === 'closed_won') await this.appendActivity(leadId, { kind: 'system', type: 'lead_won' });
    if (stage === 'closed_lost') await this.appendActivity(leadId, { kind: 'system', type: 'lead_lost' });
    // Parking a lead in nurture arms its wake-up plan (J+30) — fire and
    // forget, the stage change itself must never fail on this.
    if (stage === 'nurture') {
      void this.startLeadCadence(leadId, 'nurture').catch(() => undefined);
    }
  }

  async deleteLead(leadId: string): Promise<void> {
    await deleteDoc(doc(this.db, `${this.leadsPath()}/${leadId}`));
  }

  // --- Lead activity timeline (subcollection, client-side) -----------------

  leadActivities$(leadId: string): Observable<LeadActivity[]> {
    const ref = collection(this.db, `${this.leadsPath()}/${leadId}/activities`);
    return collectionData(query(ref, orderBy('createdAt', 'desc'), limit(LEAD_ACTIVITY_LIMIT)), {
      idField: 'activityId',
    }) as Observable<LeadActivity[]>;
  }

  /**
   * Record a manual note / call / email; for call & email also bump
   * lastContactedAt and stamp firstContactedAt once (the speed-to-lead metric).
   */
  async logLeadActivity(leadId: string, type: ManualLeadActivityType, body?: string): Promise<void> {
    await this.appendActivity(leadId, { kind: 'manual', type, body });
    if (type === 'call' || type === 'email') {
      const snap = await getDoc(doc(this.db, `${this.leadsPath()}/${leadId}`));
      const alreadyContacted = !!(snap.data() as AgencyLead | undefined)?.firstContactedAt;
      await this.updateLead(leadId, {
        lastContactedAt: serverTimestamp() as never,
        ...(alreadyContacted ? {} : { firstContactedAt: serverTimestamp() as never }),
      });
      // Pause-on-contact: a human reached the prospect — the running cadence
      // (if any) stops until a human relaunches one. Best-effort.
      try {
        await this.cancelOpenCadenceTasks(leadId);
      } catch {
        /* never fail the log for this */
      }
    }
  }

  /** Cancel every open cadence-generated task of a lead (pause-on-contact). */
  private async cancelOpenCadenceTasks(leadId: string): Promise<void> {
    const snap = await getDocs(
      query(collection(this.db, this.leadTasksPath(leadId)), where('status', '==', 'open')),
    );
    for (const d of snap.docs) {
      if (!(d.data() as { cadenceId?: string }).cadenceId) continue;
      await updateDoc(d.ref, {
        status: 'cancelled',
        cancelledReason: 'contact_logged',
        updatedAt: serverTimestamp(),
      });
    }
  }

  /** Start a pre-built follow-up plan on a lead (server-side catalogue). */
  async startLeadCadence(
    leadId: string,
    planId: 'new_lead' | 'post_viewing' | 'nurture',
  ): Promise<{ started: boolean; tasks: number }> {
    const fn = httpsCallable(this.functions, 'startLeadCadence');
    const res = await fn({ workspaceId: this.agencyWorkspaceId, leadId, planId });
    return res.data as { started: boolean; tasks: number };
  }

  /** Concierge send: email ONE matched listing to the lead (never twice the same). */
  async sendListingSuggestion(
    leadId: string,
    propertyId: string,
  ): Promise<{ sent: boolean; reason?: string }> {
    const fn = httpsCallable(this.functions, 'sendListingSuggestion');
    const res = await fn({ workspaceId: this.agencyWorkspaceId, leadId, propertyId });
    return res.data as { sent: boolean; reason?: string };
  }

  /** Hand a lead to another agent, with an auditable timeline entry. */
  async reassignLead(leadId: string, agentId: string): Promise<void> {
    await this.updateLead(leadId, { assignedAgentId: agentId });
    await this.appendActivity(leadId, { kind: 'system', type: 'reassigned' });
  }

  /** System 'viewing' entry once a visit completes — the visit becomes part of the lead's history. */
  async logViewingCompleted(
    leadId: string,
    meta: { viewingId?: string; propertyId?: string },
    body?: string,
  ): Promise<void> {
    await this.appendActivity(leadId, { kind: 'system', type: 'viewing', body, meta });
  }

  /** Append one immutable entry to the lead's activity feed, stamping author + time. */
  private async appendActivity(
    leadId: string,
    a: { kind: LeadActivityKind; type: LeadActivityType; body?: string; meta?: LeadActivityMeta },
  ): Promise<void> {
    const ref = doc(collection(this.db, `${this.leadsPath()}/${leadId}/activities`));
    const uid = this.authStore.user()?.userUID;
    const members = (this.workspaceStore.currentWorkspace()?.members ?? {}) as Record<
      string,
      { displayName?: string; email?: string }
    >;
    const data: Record<string, unknown> = {
      activityId: ref.id,
      workspaceId: this.agencyWorkspaceId,
      leadId,
      kind: a.kind,
      type: a.type,
      createdAt: serverTimestamp(),
    };
    if (a.body) data['body'] = a.body;
    if (a.meta) {
      const m: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(a.meta)) if (v !== undefined) m[k] = v;
      if (Object.keys(m).length) data['meta'] = m;
    }
    if (uid) data['authorId'] = uid;
    const authorName = uid ? members[uid]?.displayName || members[uid]?.email : undefined;
    if (authorName) data['authorName'] = authorName;
    await setDoc(ref, data);
  }

  // -------------------------------------------------------------------------
  // Lead reminders / tasks (subcollection, client-side)
  // -------------------------------------------------------------------------

  private leadTasksPath(leadId: string): string {
    return `${this.leadsPath()}/${leadId}/agency_lead_tasks`;
  }

  leadTasks$(leadId: string): Observable<LeadTask[]> {
    const ref = collection(this.db, this.leadTasksPath(leadId));
    return collectionData(query(ref, orderBy('dueAt', 'asc')), {
      idField: 'taskId',
    }) as Observable<LeadTask[]>;
  }

  /**
   * All OPEN reminders across the agency's leads — drives the dashboard "to-do".
   * Needs the `agency_lead_tasks` collectionGroup composite index
   * (workspaceId ASC + status ASC + dueAt ASC) declared in firestore.indexes.json.
   */
  openLeadTasks$(): Observable<LeadTask[]> {
    const ref = collectionGroup(this.db, 'agency_lead_tasks');
    return collectionData(
      query(
        ref,
        where('workspaceId', '==', this.agencyWorkspaceId),
        where('status', '==', 'open'),
        orderBy('dueAt', 'asc'),
      ),
      { idField: 'taskId' },
    ) as Observable<LeadTask[]>;
  }

  async createLeadTask(
    leadId: string,
    input: { title: string; dueAt: Date; assignedAgentId?: string; notes?: string },
  ): Promise<string> {
    const ref = doc(collection(this.db, this.leadTasksPath(leadId)));
    const data: Record<string, unknown> = {
      taskId: ref.id,
      workspaceId: this.agencyWorkspaceId,
      leadId,
      title: input.title,
      dueAt: input.dueAt,
      status: 'open',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    if (input.notes) data['notes'] = input.notes;
    if (input.assignedAgentId) data['assignedAgentId'] = input.assignedAgentId;
    const uid = this.authStore.user()?.userUID;
    if (uid) data['createdBy'] = uid;
    await setDoc(ref, data);
    return ref.id;
  }

  async completeLeadTask(leadId: string, taskId: string): Promise<void> {
    await updateDoc(doc(this.db, `${this.leadTasksPath(leadId)}/${taskId}`), {
      status: 'done',
      completedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  async cancelLeadTask(leadId: string, taskId: string): Promise<void> {
    await updateDoc(doc(this.db, `${this.leadTasksPath(leadId)}/${taskId}`), {
      status: 'cancelled',
      updatedAt: serverTimestamp(),
    });
  }

  /** Push a task's due date `days` ahead of today (re-arming the sweep reminder). */
  async snoozeLeadTask(leadId: string, taskId: string, days: number): Promise<void> {
    const due = new Date();
    due.setDate(due.getDate() + days);
    due.setHours(9, 0, 0, 0);
    await updateDoc(doc(this.db, `${this.leadTasksPath(leadId)}/${taskId}`), {
      dueAt: due,
      reminderSentAt: deleteField(),
      updatedAt: serverTimestamp(),
    });
  }

  // -------------------------------------------------------------------------
  // Deals (dealflow) — client-side CRUD (rules: canEditWorkspace via catch-all)
  // -------------------------------------------------------------------------

  private dealsPath(): string {
    return `workspaces/${this.agencyWorkspaceId}/agency_deals`;
  }

  deals$(): Observable<AgencyDeal[]> {
    const ref = collection(this.db, this.dealsPath());
    return collectionData(query(ref, orderBy('updatedAt', 'desc'), limit(AGENCY_LIST_LIMIT)), {
      idField: 'dealId',
    }) as Observable<AgencyDeal[]>;
  }

  deal$(dealId: string): Observable<AgencyDeal | undefined> {
    return docData(doc(this.db, `${this.dealsPath()}/${dealId}`), {
      idField: 'dealId',
    }) as Observable<AgencyDeal | undefined>;
  }

  /** Spawn a deal with the default milestone checklist for its type, and log it on the lead. */
  async createDeal(input: {
    type: DealType;
    leadId?: string;
    propertyId?: string;
    mandateId?: string;
    title?: string;
    contacts?: AgencyLead['contacts'];
    assignedAgentId?: string;
  }): Promise<string> {
    const ref = doc(collection(this.db, this.dealsPath()));
    const dealId = ref.id;
    const data: Record<string, unknown> = {
      dealId,
      workspaceId: this.agencyWorkspaceId,
      type: input.type,
      status: 'active',
      contacts: input.contacts ?? [],
      milestones: defaultDealMilestones(input.type),
      financials: { currency: 'EUR' },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    if (input.title) data['title'] = input.title;
    if (input.leadId) data['leadId'] = input.leadId;
    if (input.propertyId) data['propertyId'] = input.propertyId;
    if (input.mandateId) data['mandateId'] = input.mandateId;
    if (input.assignedAgentId) data['assignedAgentId'] = input.assignedAgentId;
    await setDoc(ref, data);
    if (input.leadId) {
      await this.appendActivity(input.leadId, { kind: 'system', type: 'deal_created', meta: { dealId } });
    }
    return dealId;
  }

  async updateDeal(dealId: string, patch: Partial<AgencyDeal>): Promise<void> {
    await updateDoc(doc(this.db, `${this.dealsPath()}/${dealId}`), {
      ...deepStripUndefined(patch),
      updatedAt: serverTimestamp(),
    });
  }

  async deleteDeal(dealId: string): Promise<void> {
    await deleteDoc(doc(this.db, `${this.dealsPath()}/${dealId}`));
  }

  // -------------------------------------------------------------------------
  // Estimations — "avis de valeur" (client-side CRUD; covered by the generic
  // agency subcollection rule, like leads/deals)
  // -------------------------------------------------------------------------

  estimations$(): Observable<AgencyEstimation[]> {
    const ref = collection(this.db, this.estimationsPath());
    return collectionData(query(ref, orderBy('updatedAt', 'desc'), limit(AGENCY_LIST_LIMIT)), {
      idField: 'estimationId',
    }) as Observable<AgencyEstimation[]>;
  }

  estimation$(estimationId: string): Observable<AgencyEstimation | undefined> {
    return docData(doc(this.db, `${this.estimationsPath()}/${estimationId}`), {
      idField: 'estimationId',
    }) as Observable<AgencyEstimation | undefined>;
  }

  async createEstimation(input: Partial<AgencyEstimation>): Promise<string> {
    const ref = doc(collection(this.db, this.estimationsPath()));
    const estimationId = ref.id;
    const clean = deepStripUndefined({
      ...input,
      estimationId,
      workspaceId: this.agencyWorkspaceId,
      status: input.status ?? 'draft',
    });
    await setDoc(ref, { ...clean, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    return estimationId;
  }

  async updateEstimation(estimationId: string, patch: Partial<AgencyEstimation>): Promise<void> {
    const clean = deepStripUndefined(patch);
    await updateDoc(doc(this.db, `${this.estimationsPath()}/${estimationId}`), {
      ...clean,
      updatedAt: serverTimestamp(),
    });
  }

  async deleteEstimation(estimationId: string): Promise<void> {
    await deleteDoc(doc(this.db, `${this.estimationsPath()}/${estimationId}`));
  }

  /** Ask the AI to produce a professional sale + rental valuation. Writes the
   *  result onto the estimation server-side and returns it. */
  async generateEstimationValuation(
    estimationId: string,
    language?: string,
  ): Promise<EstimationAiValuation> {
    const fn = httpsCallable<
      { workspaceId: string; estimationId: string; language?: string },
      { success: boolean; valuation: EstimationAiValuation; model: string }
    >(this.functions, 'generateEstimationValuation');
    const res = await fn({ workspaceId: this.agencyWorkspaceId, estimationId, language });
    return { ...res.data.valuation, model: res.data.model };
  }

  /** Mint a public token + email the owner the avis de valeur. Marks it sent. */
  async sendEstimationReport(
    estimationId: string,
    recipientEmail?: string,
    language?: string,
  ): Promise<{ sent: boolean; url: string }> {
    const fn = httpsCallable<
      { workspaceId: string; estimationId: string; recipientEmail?: string; language?: string },
      { success: boolean; sent: boolean; messageId: string | null; url: string }
    >(this.functions, 'sendEstimationReport');
    const res = await fn({ workspaceId: this.agencyWorkspaceId, estimationId, recipientEmail, language });
    return { sent: res.data.sent, url: res.data.url };
  }

  /** Mint (or reuse) the agency's public estimation-request link. */
  async createEstimationRequestLink(): Promise<{ token: string; url: string }> {
    const fn = httpsCallable<
      { workspaceId: string },
      { success: boolean; token: string; url: string }
    >(this.functions, 'createEstimationRequestLink');
    const res = await fn({ workspaceId: this.agencyWorkspaceId });
    return { token: res.data.token, url: res.data.url };
  }

  // -------------------------------------------------------------------------
  // Viewings (scheduling) — client-side CRUD (rules: canEditWorkspace)
  // -------------------------------------------------------------------------

  viewings$(): Observable<Viewing[]> {
    const ref = collection(this.db, this.viewingsPath());
    // Date-windowed instead of the whole `agency_viewings` history: the landing
    // dashboard, my-day and team-performance only need recent + upcoming
    // viewings. `where` + `orderBy` on the SAME field → no composite index; a
    // naive `limit` was wrong here (it would drop FUTURE viewings). Arbitrary
    // past ranges (the calendar) go through `viewingsInRange$`. Window size is
    // unit-pinned in `viewings-window.util`.
    const cutoff = viewingsWindowStart(new Date());
    return collectionData(
      query(ref, where('scheduledAt', '>=', cutoff), orderBy('scheduledAt', 'asc')),
      { idField: 'viewingId' },
    ) as Observable<Viewing[]>;
  }

  /**
   * Viewings scheduled within an explicit `[from, to]` window — for the calendar
   * (the visible period ± a buffer, re-queried on navigation) and any
   * period-scoped report. `where` + `orderBy` on the SAME `scheduledAt` field →
   * no composite index required.
   */
  viewingsInRange$(from: Date, to: Date): Observable<Viewing[]> {
    const ref = collection(this.db, this.viewingsPath());
    return collectionData(
      query(
        ref,
        where('scheduledAt', '>=', from),
        where('scheduledAt', '<=', to),
        orderBy('scheduledAt', 'asc'),
      ),
      { idField: 'viewingId' },
    ) as Observable<Viewing[]>;
  }

  /**
   * Viewings booked for a single lead — used by the lead-detail page. A single
   * equality filter (`leadId ==`) is auto-indexed (no composite index needed)
   * and reads only this lead's viewings instead of streaming the whole
   * `agency_viewings` history into a page that only ever shows one lead. The
   * caller sorts client-side, so no `orderBy` (and thus no composite index)
   * is required here.
   */
  viewingsForLead$(leadId: string): Observable<Viewing[]> {
    const ref = collection(this.db, this.viewingsPath());
    return collectionData(query(ref, where('leadId', '==', leadId)), {
      idField: 'viewingId',
    }) as Observable<Viewing[]>;
  }

  // -------------------------------------------------------------------------
  // Per-agent scoped reads — power "Ma journée" without streaming the whole
  // agency pipeline. Single-field equality (`assignedAgentId == uid`) → auto
  // single-field index, no composite needed; the UI sorts/filters in memory.
  // -------------------------------------------------------------------------

  /**
   * Read a collection scoped to one agent (`assignedAgentId == agentId`). Single-
   * field equality → auto single-field index, no composite needed; the UI
   * sorts/filters in memory. No fallback: a query error surfaces so the scoped
   * read can be validated (it must not silently revert to a full-collection scan).
   */
  private scopedByAgent$<T>(
    path: string,
    idField: string,
    agentId: string,
  ): Observable<T[]> {
    const ref = collection(this.db, path);
    return collectionData(query(ref, where('assignedAgentId', '==', agentId)), { idField }) as Observable<T[]>;
  }

  viewingsForAgent$(agentId: string): Observable<Viewing[]> {
    return this.scopedByAgent$<Viewing>(this.viewingsPath(), 'viewingId', agentId);
  }

  dealsForAgent$(agentId: string): Observable<AgencyDeal[]> {
    return this.scopedByAgent$<AgencyDeal>(this.dealsPath(), 'dealId', agentId);
  }

  mandatesForAgent$(agentId: string): Observable<AgencyMandate[]> {
    return this.scopedByAgent$<AgencyMandate>(this.mandatesPath(), 'mandateId', agentId);
  }

  estimationsForAgent$(agentId: string): Observable<AgencyEstimation[]> {
    return this.scopedByAgent$<AgencyEstimation>(this.estimationsPath(), 'estimationId', agentId);
  }

  viewing$(viewingId: string): Observable<Viewing | undefined> {
    return docData(doc(this.db, `${this.viewingsPath()}/${viewingId}`), {
      idField: 'viewingId',
    }) as Observable<Viewing | undefined>;
  }

  async createViewing(input: Partial<Viewing>): Promise<string> {
    const ref = doc(collection(this.db, this.viewingsPath()));
    const viewingId = ref.id;
    await setDoc(ref, {
      ...deepStripUndefined({
        ...input,
        viewingId,
        workspaceId: this.agencyWorkspaceId,
        status: input.status ?? 'confirmed',
      }),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return viewingId;
  }

  async updateViewing(viewingId: string, patch: Partial<Viewing>): Promise<void> {
    await updateDoc(doc(this.db, `${this.viewingsPath()}/${viewingId}`), {
      ...deepStripUndefined(patch),
      updatedAt: serverTimestamp(),
    });
  }

  async deleteViewing(viewingId: string): Promise<void> {
    await deleteDoc(doc(this.db, `${this.viewingsPath()}/${viewingId}`));
  }

  // -------------------------------------------------------------------------
  // Commercial organisation — agency roles, manager hierarchy, teams
  // (read mirror; all writes go through callables — docs are server-only-write)
  // -------------------------------------------------------------------------

  /** Business profiles (agency role / manager / team) for every member. */
  teamMembers$(): Observable<AgencyTeamMember[]> {
    const ref = collection(this.db, this.teamMembersPath());
    return collectionData(ref, { idField: 'uid' }) as Observable<AgencyTeamMember[]>;
  }

  /** Teams / branches of the agency. */
  teams$(): Observable<AgencyTeam[]> {
    const ref = collection(this.db, this.teamsPath());
    return collectionData(query(ref, orderBy('name', 'asc')), {
      idField: 'teamId',
    }) as Observable<AgencyTeam[]>;
  }

  /** Set a member's agency role / manager / team / title (null clears a field). */
  async setAgencyMemberRole(
    memberId: string,
    input: AgencyMemberRoleInput,
  ): Promise<void> {
    const fn = httpsCallable<
      { workspaceId: string; memberId: string } & AgencyMemberRoleInput,
      { success: boolean }
    >(this.functions, 'setAgencyMemberRole');
    await fn({ workspaceId: this.agencyWorkspaceId, memberId, ...input });
  }

  /** Create (omit teamId) or update (provide teamId) a team / branch. */
  async upsertAgencyTeam(input: AgencyTeamInput): Promise<string> {
    const fn = httpsCallable<
      { workspaceId: string } & AgencyTeamInput,
      { success: boolean; teamId: string }
    >(this.functions, 'upsertAgencyTeam');
    const res = await fn({ workspaceId: this.agencyWorkspaceId, ...input });
    return res.data.teamId;
  }

  /** Delete a team; members assigned to it are unassigned server-side. */
  async deleteAgencyTeam(teamId: string): Promise<void> {
    const fn = httpsCallable<
      { workspaceId: string; teamId: string },
      { success: boolean; unassigned: number }
    >(this.functions, 'deleteAgencyTeam');
    await fn({ workspaceId: this.agencyWorkspaceId, teamId });
  }

  // -------------------------------------------------------------------------
  // Portal connectors — read mirror (server-only write via callables)
  // -------------------------------------------------------------------------

  connectors$(): Observable<PortalConnector[]> {
    const ref = collection(this.db, this.connectorsPath());
    return collectionData(ref, { idField: 'portalId' }) as Observable<PortalConnector[]>;
  }

  connector$(portalId: PortalId): Observable<PortalConnector | undefined> {
    return docData(doc(this.db, `${this.connectorsPath()}/${portalId}`), {
      idField: 'portalId',
    }) as Observable<PortalConnector | undefined>;
  }

  async savePortalCredentials(input: {
    portalId: PortalId; accountLabel?: string; offererId?: string; username?: string; secret?: string;
  }): Promise<void> {
    const fn = httpsCallable<{ workspaceId: string } & typeof input, { success: boolean }>(
      this.functions, 'savePortalCredentials',
    );
    await fn({ workspaceId: this.agencyWorkspaceId, ...input });
  }

  async disconnectPortal(portalId: PortalId): Promise<void> {
    const fn = httpsCallable<{ workspaceId: string; portalId: PortalId }, { success: boolean }>(
      this.functions, 'disconnectPortal',
    );
    await fn({ workspaceId: this.agencyWorkspaceId, portalId });
  }

  async pushPropertyToPortal(input: {
    portalId: PortalId; propertyId: string; action?: 'publish' | 'update' | 'withdraw';
  }): Promise<{ externalRef: string }> {
    const fn = httpsCallable<
      { workspaceId: string; portalId: PortalId; propertyId: string; action?: string },
      { success: boolean; stubbed: boolean; externalRef: string }
    >(this.functions, 'pushPropertyToPortal');
    const res = await fn({ workspaceId: this.agencyWorkspaceId, ...input });
    return { externalRef: res.data.externalRef };
  }

  // -------------------------------------------------------------------------
  // AI — multilingual listing descriptions
  // -------------------------------------------------------------------------

  async generateDescriptions(attributes: Record<string, unknown>): Promise<{
    nl?: string; fr?: string; de?: string; en?: string;
  }> {
    const fn = httpsCallable<
      { workspaceId: string; attributes: Record<string, unknown> },
      { success: boolean; descriptions: { nl?: string; fr?: string; de?: string; en?: string } }
    >(this.functions, 'generateListingDescriptions');
    const res = await fn({ workspaceId: this.agencyWorkspaceId, attributes });
    return res.data.descriptions;
  }

  // -------------------------------------------------------------------------
  // Online booking — shareable per-listing link + confirmation email
  // -------------------------------------------------------------------------

  /** Mint (or fetch) the public booking link for a listing. */
  async createPropertyBookingLink(propertyId: string): Promise<{ token: string; url: string }> {
    const fn = httpsCallable<
      { workspaceId: string; propertyId: string },
      { success: boolean; token: string; url: string }
    >(this.functions, 'createPropertyBookingLink');
    const res = await fn({ workspaceId: this.agencyWorkspaceId, propertyId });
    return { token: res.data.token, url: res.data.url };
  }

  /** Send the confirmation email for a viewing (used when confirming a draft). */
  async sendViewingConfirmation(viewingId: string): Promise<{ sent: boolean }> {
    const fn = httpsCallable<
      { workspaceId: string; viewingId: string },
      { success: boolean; sent: boolean }
    >(this.functions, 'sendViewingConfirmation');
    const res = await fn({ workspaceId: this.agencyWorkspaceId, viewingId });
    return { sent: res.data.sent };
  }
}
